/**
 * Jun-2026 (User-Wunsch) — Auto-Speichern beim Verlassen eines Feldes (onBlur)
 * für die Dispo-Modals. React-`onBlur` bubbelt (focusout) aus allen Kind-Inputs,
 * daher genügt ein `onBlur`-Handler am Modal-Container (DialogContent).
 *
 * - `saveSilent`: async () => boolean   (still speichern, KEINE Toasts; gibt
 *   `false` zurück, wenn nicht gespeichert/ungültig — dann bleibt der Baseline)
 * - `getKey`: () => string              (serialisierter Zustand der Felder)
 * - `enabled`: aktiviert/deaktiviert das Auto-Speichern
 *
 * Liefert `{ onBlur, status, savedAt, markSaved }` mit status ∈ idle|saving|saved.
 * `markSaved()` kann nach einem manuellen „Aktualisieren" aufgerufen werden, um
 * den „zuletzt gespeichert um HH:MM"-Hinweis zu aktualisieren.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Check } from "lucide-react";

export function useAutosaveOnBlur(saveSilent, getKey, { enabled = true } = {}) {
    const lastKey = useRef(null);
    const busy = useRef(false);
    const [status, setStatus] = useState("idle");
    const [savedAt, setSavedAt] = useState(null);

    // Baseline beim Mount setzen, damit ein Blur OHNE Änderung nicht speichert.
    useEffect(() => {
        try { lastKey.current = getKey(); } catch { lastKey.current = null; }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const markSaved = useCallback(() => {
        try { lastKey.current = getKey(); } catch { /* noop */ }
        setStatus("saved");
        setSavedAt(new Date());
    }, [getKey]);

    const onBlur = useCallback(async () => {
        if (!enabled || busy.current) return;
        let key;
        try { key = getKey(); } catch { return; }
        if (key === lastKey.current) return;
        busy.current = true;
        // WICHTIG: NICHT synchron im focusout-Ereignis speichern. Ein setState
        // (setStatus) während des Fokuswechsels lässt den Radix-Dialog-FocusScope
        // den Fokus vom gerade angeklickten Feld zurückstehlen → „Eingabe-Freeze
        // im nächsten Feld". Deshalb erst nach dem Fokuswechsel (Timeout) speichern.
        await new Promise((r) => setTimeout(r, 0));
        setStatus("saving");
        try {
            const ok = await saveSilent();
            if (ok !== false) { lastKey.current = key; setStatus("saved"); setSavedAt(new Date()); }
            else { setStatus("idle"); }
        } catch {
            setStatus("idle");
        } finally {
            busy.current = false;
        }
    }, [saveSilent, getKey, enabled]);

    return { onBlur, status, savedAt, markSaved };
}

const fmtTime = (d) => {
    try { return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
};

export function AutosaveBadge({ status, savedAt }) {
    let inner;
    let color;
    if (status === "saving") {
        inner = <><Loader2 className="w-3 h-3 animate-spin" /> Speichert…</>;
        color = "text-orange-500";
    } else if (status === "saved") {
        inner = <><Check className="w-3 h-3" /> {savedAt ? `Zuletzt gespeichert um ${fmtTime(savedAt)}` : "Automatisch gespeichert"}</>;
        color = "text-emerald-600";
    } else {
        inner = <>Auto-Speichern aktiv</>;
        color = "text-gray-400";
    }
    return (
        <div className="flex justify-end -mt-1 mb-0.5">
            <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${color}`} data-testid="dispo-autosave-badge">{inner}</span>
        </div>
    );
}
